/* SubcategoryPage - Shows all products in a specific category + subcategory combination
 * Created: 2026-08-03 - Displays products filtered by /:categorySlug/:subcategorySlug
 * Card style matches CategoryPage product grid with alternating corner radii
 * Updated: 2026-08-23 - 3-column grid with larger cards for borderpakketten, vaste planten, bodembedekkers
 * Updated: Added DataFast subcategory_viewed goal.
 */
import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { calculatePriceInclBTW } from '@/lib/btw-calculator';
import { getProductImageUrl } from '@/lib/image-url';
import ContactBar from '@/components/ContactBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import { Loader as Loader2, Search, Package, ArrowRight } from 'lucide-react';
import EmptyState from '@/components/EmptyState';
import { trackGoal } from '@/lib/datafast';

interface ProductCard {
  id: string;
  name: string;
  scientific_name: string;
  base_slug: string;
  geslacht: string;
  sort: string;
  image_url: string | null;
  lowest_price_incl_btw: number;
  variant_count: number;
  types: string[];
  btw_percentage: number;
}

const CATEGORY_META: Record<string, { title: string }> = {
  haagplanten: { title: 'Haagplanten' },
  bomen: { title: 'Bomen' },
  tuinplanten: { title: 'Tuinplanten' },
};

interface SubcategoryPageProps {
  categoryId: string;
  subcategoryId: string;
  subcategoryName: string;
}

function SubcategoryPage({ categoryId, subcategoryId, subcategoryName: resolvedName }: SubcategoryPageProps) {
  const params = useParams<{ category: string; product: string }>();
  const categorySlug = params.category;
  const subcategorySlug = params.product;
  const [products, setProducts] = useState<ProductCard[]>([]);
  const [subcategoryName, setSubcategoryName] = useState(resolvedName || '');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (categoryId && subcategoryId) {
      setSearchQuery('');
      setProducts([]);
      setSubcategoryName(resolvedName || '');
      fetchData(categoryId, subcategoryId);
    }
  }, [categoryId, subcategoryId, resolvedName]);

  useEffect(() => {
    if (subcategoryName) trackGoal('subcategory_viewed', { subcategory: subcategoryName });
  }, [subcategoryName]);

  const fetchData = async (catId: string, subId: string) => {
    try {
      setLoading(true);

      const { data: productsData, error } = await supabase
        .from('products')
        .select(`
          id,
          name,
          scientific_name,
          base_slug,
          geslacht,
          sort,
          btw_percentage,
          is_simple_product,
          simple_price
        `)
        .eq('active', true)
        .eq('category_id', catId)
        .eq('subcategory_id', subId)
        .order('name');

      if (error) throw error;
      if (!productsData || productsData.length === 0) {
        setLoading(false);
        return;
      }

      const productCards: ProductCard[] = await (async () => {
        const productIds = productsData.map((p: any) => p.id);
        const nonSimpleIds = productsData.filter((p: any) => !p.is_simple_product).map((p: any) => p.id);

        const [variantsResult, imagesResult] = await Promise.all([
          nonSimpleIds.length
            ? supabase
                .from('product_variants')
                .select('product_id, price_tier_1, type')
                .in('product_id', nonSimpleIds)
                .eq('active', true)
            : Promise.resolve({ data: [] as any[] }),
          productIds.length
            ? supabase
                .from('product_images')
                .select('product_id, storage_path, uses_new_bucket')
                .in('product_id', productIds)
                .is('variant_id', null)
                .eq('is_primary', true)
            : Promise.resolve({ data: [] as any[] }),
        ]);

        const variantsByProduct = new Map<string, any[]>();
        (variantsResult.data || []).forEach((v: any) => {
          if (!variantsByProduct.has(v.product_id)) variantsByProduct.set(v.product_id, []);
          variantsByProduct.get(v.product_id)!.push(v);
        });
        const primaryImageByProduct = new Map<string, any>();
        (imagesResult.data || []).forEach((img: any) => {
          if (!primaryImageByProduct.has(img.product_id)) primaryImageByProduct.set(img.product_id, img);
        });

        return productsData.map((p: any) => {
          const imageData = primaryImageByProduct.get(p.id);
          const image_url = imageData ? getProductImageUrl(imageData.storage_path, imageData.uses_new_bucket) : null;

          if (p.is_simple_product) {
            const btwPercentage = p.btw_percentage || 21;
            const priceExcl = parseFloat(p.simple_price || '0');
            const priceIncl = calculatePriceInclBTW(priceExcl, btwPercentage);
            return {
              id: p.id,
              name: p.name,
              scientific_name: p.scientific_name || '',
              base_slug: p.base_slug,
              geslacht: p.geslacht || '',
              sort: p.sort || '',
              image_url,
              lowest_price_incl_btw: priceIncl,
              variant_count: 1,
              types: [],
              btw_percentage: btwPercentage,
            };
          }

          const variants = variantsByProduct.get(p.id) || [];
          const prices = variants
            .map((v: any) => parseFloat(v.price_tier_1 || '0'))
            .filter((pr: number) => pr > 0);
          const lowestPrice = prices.length > 0 ? Math.min(...prices) : 0;
          const btwPercentage = p.btw_percentage || 9;
          const lowestPriceInclBtw = calculatePriceInclBTW(lowestPrice, btwPercentage);
          const types = Array.from(new Set(variants.map((v: any) => v.type)));

          return {
            id: p.id,
            name: p.name,
            scientific_name: p.scientific_name || '',
            base_slug: p.base_slug,
            geslacht: p.geslacht || '',
            sort: p.sort || '',
            image_url,
            lowest_price_incl_btw: lowestPriceInclBtw,
            variant_count: variants.length,
            types,
            btw_percentage: btwPercentage,
          };
        });
      })();

      setProducts(productCards);
    } catch (err) {
      console.error('Error fetching subcategory products:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(p =>
    !searchQuery.trim() ||
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.scientific_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.geslacht.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const catMeta = CATEGORY_META[categorySlug || ''] || { title: categorySlug };
  const pageTitle = subcategoryName || subcategorySlug || '';
  const canonicalUrl = `https://buitnplanten.nl/${categorySlug}/${subcategorySlug}`;

  const slugsLower = (subcategorySlug || '').toLowerCase();
  const nameLower = (subcategoryName || '').toLowerCase();
  const isLargeCardSub =
    slugsLower.includes('borderpakket') || nameLower.includes('borderpakket') ||
    slugsLower.includes('vaste-plant') || nameLower.includes('vaste plant') ||
    slugsLower.includes('bodembedekker') || nameLower.includes('bodembedekker');

  if (loading) {
    return (
      <>
        <Helmet>
          <title>{pageTitle} - Buitn Planten</title>
        </Helmet>
        <ContactBar />
        <div className="min-h-screen flex items-center justify-center bg-white pt-0">
          <Loader2 className="h-12 w-12 animate-spin text-jv-primary" />
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle} - Buitn Planten</title>
        <meta name="description" content={`Bekijk ons assortiment ${pageTitle.toLowerCase()} bij Buitn Planten.`} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={`${pageTitle} - Buitn Planten`} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={canonicalUrl} />

        {/* BreadcrumbList Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://buitnplanten.nl/" },
              { "@type": "ListItem", "position": 2, "name": catMeta.title, "item": `https://buitnplanten.nl/${categorySlug}` },
              { "@type": "ListItem", "position": 3, "name": pageTitle, "item": canonicalUrl }
            ]
          })}
        </script>

        {/* CollectionPage Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": pageTitle,
            "description": `Bekijk ons assortiment ${pageTitle.toLowerCase()} bij Buitn Planten.`,
            "url": canonicalUrl,
            "isPartOf": {
              "@type": "WebSite",
              "name": "Buitn Planten",
              "url": "https://buitnplanten.nl/"
            },
            "mainEntity": {
              "@type": "ItemList",
              "itemListElement": filteredProducts.map((product, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "url": `https://buitnplanten.nl/${product.base_slug}`,
                "name": product.name
              }))
            }
          })}
        </script>
      </Helmet>
      <ContactBar />
      <div className="min-h-screen bg-gradient-to-b from-white to-[#edf5eb]">
        <Header />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
          <PageBreadcrumb items={[{ label: 'Home', href: '/' }, { label: catMeta.title, href: `/${categorySlug}` }, { label: pageTitle }]} />

          <div className="mb-10">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-jv-primary mb-4">
              {pageTitle}
            </h1>
            <p className="text-lg md:text-xl text-gray-700 max-w-2xl">
              Bekijk al onze beschikbare {pageTitle.toLowerCase()}
            </p>
          </div>

          {products.length > 3 && (
            <div className="mb-8 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Zoek op naam..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-jv-primary focus:border-transparent bg-white shadow-sm"
                />
              </div>
            </div>
          )}

          {filteredProducts.length === 0 ? (
            <EmptyState
              message={searchQuery ? 'Geen producten gevonden.' : 'Er zijn nog geen producten in deze subcategorie.'}
              actionLabel={searchQuery ? 'Zoeken wissen' : undefined}
              onAction={searchQuery ? () => setSearchQuery('') : undefined}
            />
          ) : (
            <div className={isLargeCardSub
              ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10"
              : "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 lg:gap-8"
            }>
              {filteredProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                >
                  <Link
                    to={`/${product.base_slug}`}
                    className="group flex flex-col rounded-xl overflow-hidden border border-transparent hover:ring-2 hover:ring-buitn-bosgroen transition-all duration-200 bg-white"
                    style={{ ['--tw-ring-offset-color' as any]: '#fcfaf6', ['--tw-ring-offset-width' as any]: '3px' }}
                  >
                    <div className={isLargeCardSub ? "aspect-[4/3] overflow-hidden relative" : "aspect-[4/3] overflow-hidden relative"}>
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          width={400}
                          height={300}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-[#e7e1d6]">
                          <Package className={isLargeCardSub ? "h-12 w-12 text-[#cfcac2]" : "h-10 w-10 text-[#cfcac2]"} />
                          <span className="text-xs text-[#cfcac2]">Foto volgt</span>
                        </div>
                      )}
                      {product.lowest_price_incl_btw > 0 && (
                        <div className={isLargeCardSub
                          ? "absolute top-3 right-3 rounded-[4px] bg-[#1e2b22] px-3 py-1.5 text-sm font-medium text-[#e7e1d6]"
                          : "absolute top-3 right-3 rounded-[4px] bg-[#1e2b22] px-2.5 py-1 text-xs font-medium text-[#e7e1d6]"
                        }>
                          Vanaf €{product.lowest_price_incl_btw.toFixed(2)}
                        </div>
                      )}
                    </div>
                    <div className={isLargeCardSub
                      ? "flex-1 px-4 py-3.5 bg-white border-t border-gray-100 flex items-center justify-between gap-2"
                      : "flex-1 px-3 py-2.5 bg-white border-t border-gray-100 flex items-center justify-between gap-1"
                    }>
                      <div className="min-w-0 flex-1">
                        {product.scientific_name && (
                          <p className={isLargeCardSub
                            ? "mb-1 line-clamp-1 text-xs italic text-[#cfcac2]"
                            : "mb-0.5 line-clamp-1 text-[11px] italic text-[#cfcac2]"
                          }>{product.scientific_name}</p>
                        )}
                        <span className={isLargeCardSub
                          ? "line-clamp-2 text-sm sm:text-base font-medium leading-tight text-buitn-bosgroen"
                          : "line-clamp-2 text-xs sm:text-[13px] font-medium leading-tight text-buitn-bosgroen"
                        }>
                          {product.name}
                        </span>
                      </div>
                      <ArrowRight className={isLargeCardSub
                        ? "w-4 h-4 flex-shrink-0 text-buitn-bosgroen/50 group-hover:text-buitn-bosgroen transition-colors"
                        : "w-3 h-3 flex-shrink-0 text-buitn-bosgroen/50 group-hover:text-buitn-bosgroen transition-colors"
                      } />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}


export default SubcategoryPage